import React from 'react';
import { ConfigProvider, ConfigProviderProps } from 'antd';
import type { Locale } from 'antd/es/locale';
import enGB from 'antd/es/locale/en_GB';
import esES from 'antd/es/locale/es_ES';
import frFR from 'antd/es/locale/fr_FR';
import plPL from 'antd/es/locale/pl_PL';
import roRO from 'antd/es/locale/ro_RO';
import ukUA from 'antd/es/locale/uk_UA';
// antd's DatePicker reads month/weekday names from dayjs, resolving e.g.
// `fr_FR` → `fr`, so every supported language's dayjs locale must be registered
import '@lib/utils/dayjsLocale';
import * as VARS from '@lib/assets/styles/colors';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _omit from 'lodash/omit';

// Some antd locales hardcode picker month/weekday names (`es_ES`, `uk_UA`) or
// switch the month panel to full names via `monthFormat: 'MMMM'` (`pl_PL`).
// Drop them so every language takes its names from dayjs, whose short months
// are aligned with `days.json` in `@lib/utils/dayjsLocale`
const withDayjsPickerNames = (locale: Locale): Locale => {
  if (!locale.DatePicker) return locale;
  return {
    ...locale,
    DatePicker: {
      ...locale.DatePicker,
      lang: _omit(locale.DatePicker.lang, [
        'monthFormat',
        'shortMonths',
        'shortWeekDays',
      ]),
    },
  };
};

const antdLocaleMap = {
  en: withDayjsPickerNames(enGB),
  es: withDayjsPickerNames(esES),
  fr: withDayjsPickerNames(frFR),
  pl: withDayjsPickerNames(plPL),
  ro: withDayjsPickerNames(roRO),
  uk: withDayjsPickerNames(ukUA),
};

const inputConfig = {
  borderRadius: 6,
  fontSize: 14,
  inputFontSizeLG: 14,
  inputFontSizeSM: 14,
  inputFontSizeSX: 14,
  lineHeightLG: 1.42,
  lineHeightSM: 1.42,
  lineHeightXS: 1.42,
  paddingInline: 16,
  paddingInlineLG: 16,
  paddingInlineSM: 16,
  paddingInlineXS: 16,
  paddingBlockLG: 16,
  paddingBlockSM: 16,
  paddingBlockXS: 16,
  activeShadow: `inset 0 0 0 1px ${VARS.blue500}`,
  errorActiveShadow: `inset 0 0 0 1px ${VARS.error900}`,
  hoverBorderColor: VARS.gray700,
  activeBorderColor: VARS.blue500,
  colorIcon: VARS.gray700,
  colorBgContainer: VARS.gray0,
  colorBgContainerDisabled: VARS.gray0,
  colorTextDisabled: VARS.disable500,
  colorTextPlaceholder: VARS.gray400,
  colorErrorOutline: VARS.error900,
  colorErrorBorderHover: VARS.error900,
};

const theme = {
  token: {
    borderRadiusLG: 6,
    colorPrimary: VARS.blue500,
    colorLink: VARS.success900,
    colorSuccess: VARS.success900,
    colorError: VARS.error900,
    colorErrorText: VARS.error900,
    colorErrorBgHover: VARS.error900,
    colorBorder: VARS.gray200,
    colorWarning: VARS.warning900,
    colorText: VARS.gray700,
    fontFamily: "'Inter', sans-serif",
  },
  components: {
    Input: {
      ...inputConfig,
    },
    TextArea: {
      ...inputConfig,
    },
    DatePicker: {
      ...inputConfig,
      sizePopupArrow: 0,
      zIndexPopup: 100, // $zIndexDropDown
    },
    Popover: {
      zIndexPopup: 100, // $zIndexDropDown
    },
  },
};

export default function ConfigProviderCommon(props: ConfigProviderProps) {
  const { children, ...rest } = props;
  const { i18n } = useTranslation();
  const currentLocale = i18n.language;
  const locale = _get(antdLocaleMap, currentLocale, antdLocaleMap.fr);

  return (
    <ConfigProvider {...rest} locale={locale} theme={theme}>
      {children}
    </ConfigProvider>
  );
}
